import { CurrentPersonQuery, EventDetailsQuery, FormData, RegistrationType } from "./types";
import moment from "moment";
import { convertToTimezone, readableDate } from "../../utils/dateUtils";
import { camelCase } from "lodash";

export const beforeRegistrationOpen = (event: EventDetailsQuery["event"]) => new Date() < new Date(event?.registrationOpenAt);

export const afterRegistrationClose = (event: EventDetailsQuery["event"]) => new Date() > new Date(event?.registrationCloseAt);

export const includesAllSessions = (event: EventDetailsQuery["event"]) => event.registrationType === RegistrationType.ALL;

export const isPAT = (event: EventDetailsQuery["event"]) => event.eventType.toLowerCase() === "pat";

export const isCancelled = (event: EventDetailsQuery["event"]) => !!event.cancelledAt;

export const setDefaultSessionValues = (event: EventDetailsQuery["event"]) => {
  return event.sessions.reduce<Record<string, boolean>>((acc, session) => {
    acc[session.id] = includesAllSessions(event);
    return acc;
  }, {});
};

export const selectedSessions = (sessions: FormData["sessions"]) => {
  if (!sessions) return [];
  return Object.entries(sessions).reduce<{ id: string }[]>(
    (acc, [id, isSelected]) => [...acc, ...(isSelected ? [{ id }] : [])],
    []
  );
};

const CUSTOM_FIELD_DEFAULTS: Record<string, any> = {
  dateOfBirth: (person?: CurrentPersonQuery["currentPerson"]) => person?.dateOfBirth || "",
  gender: () => "",
  disclaimer: () => false,
};

export const getFormDefaultValues = (
  person?: CurrentPersonQuery["currentPerson"],
  event?: EventDetailsQuery["event"]
): FormData => {
  const initialValues = {
    sessions: event ? setDefaultSessionValues(event) : {},
    disclaimer: false,
  };

  if (!event?.customFields) {
    return initialValues;
  }

  const customFieldValues = event.customFields.reduce(
    (acc, field) => {
      const fieldName = camelCase(field);
      const defaultValueFn = CUSTOM_FIELD_DEFAULTS[fieldName];

      if (defaultValueFn) {
        acc[fieldName] = defaultValueFn(person);
      }

      return acc;
    },
    {} as Record<string, any>
  );

  return {
    ...initialValues,
    ...customFieldValues,
  };
};

export const hasRequiredFields = (formData: FormData, requiredFields: string[]): boolean =>
  requiredFields.every((field) => formData[field as keyof FormData]);

export const formatEventDate = (startsAt: string, endsAt: string, timezone: string, includeTime = false): string => {
  const isEqual = moment(startsAt).startOf("day").isSame(moment(endsAt).startOf("day"));

  const endDate = readableDate(convertToTimezone(endsAt, timezone));
  const startDate =
    isEqual && includeTime
      ? convertToTimezone(startsAt, timezone, "MMM D, YYYY [at] h:mma z")
      : readableDate(convertToTimezone(startsAt, timezone));

  return isEqual ? startDate : `${startDate} – ${endDate}`;
};
